import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Check, X, Search } from "lucide-react";

const DELIVERY = [
  "Vendor ILL", "Vendor Broadband", "Vendor ILL + AMC",
  "Own POP – Fiber", "Own POP – RF", "Own POP – Fiber/RF under AMC",
];
const isOwnPop = (d) => (d || "").startsWith("Own POP");
const isAmc = (d) => (d || "").includes("AMC");

const field = "mt-1 w-full h-10 px-3 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500";

// Combobox that matches an existing org by name (did-you-mean) or lets you create new.
function OrgPicker({ label, matchUrl, value, onPick, onNew, testid }) {
  const [q, setQ] = useState(value?.name || "");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const box = useRef();
  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  useEffect(() => {
    if (!q || q.length < 2 || value?.id) { setRes(null); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`${matchUrl}?name=${encodeURIComponent(q)}`); setRes(data); setOpen(true); } catch (e) {}
    }, 250);
    return () => clearTimeout(t);
  }, [q, matchUrl, value]);

  const pick = (r) => { setQ(r.name); setOpen(false); onPick(r); };
  const createNew = () => { setOpen(false); onNew(q.trim()); };

  return (
    <div className="relative" ref={box}>
      <label className="text-xs font-semibold text-slate-600">{label}</label>
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid={testid} className={field + " pl-9"} placeholder="Type to search or create…"
          value={q} onChange={(e) => { setQ(e.target.value); onNew(null); }} onFocus={() => q.length >= 2 && setOpen(true)} />
        {value?.id && <Check size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-600" />}
        {value?.isNew && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">NEW</span>}
      </div>
      {open && res && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden text-sm">
          {res.exact && (
            <button type="button" onClick={() => pick(res.exact)} className="w-full text-left px-3 py-2 hover:bg-emerald-50 flex items-center gap-2 border-b">
              <Check size={14} className="text-emerald-600" /> Use existing: <b>{res.exact.name}</b>
            </button>
          )}
          {res.suggestions?.map((s) => (
            <button type="button" key={s.id} onClick={() => pick(s)} className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b">
              Did you mean <b>{s.name}</b>? <span className="text-xs text-slate-400">{Math.round(s.score * 100)}% match</span>
            </button>
          ))}
          {q.trim() && (!res.exact) && (
            <button type="button" onClick={createNew} className="w-full text-left px-3 py-2 hover:bg-amber-50 text-amber-700 font-medium">
              ＋ Create new “{q.trim()}”
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const Section = ({ n, title, note, children }) => (
  <div className="border-t border-dashed border-slate-200 pt-5 mt-5 first:border-0 first:pt-0 first:mt-0">
    <div className="flex items-center gap-2 mb-3">
      <span className="w-6 h-6 rounded-md bg-[var(--brand)] text-white text-xs font-bold grid place-items-center">{n}</span>
      <h3 className="font-head font-bold">{title}</h3>
      {note && <span className="text-[11px] font-semibold text-cyan-700 bg-cyan-50 px-2 py-0.5 rounded-full">{note}</span>}
    </div>
    {children}
  </div>
);

export default function AddCustomerLink() {
  const { can } = useAuth();
  const nav = useNavigate();
  const { connectionId } = useParams();
  const editing = !!connectionId;
  const canRates = can("manage_rates");
  const [pops, setPops] = useState([]);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ delivery_type: "Vendor ILL", billing_type: "Prepaid", amc_billing_type: "Postpaid" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  // org selections: {id,name} existing or {name,isNew}
  const [cust, setCust] = useState({});
  const [vend, setVend] = useState({});
  const [amcVend, setAmcVend] = useState({});

  useEffect(() => { api.get("/pops").then((r) => setPops(r.data)).catch(() => {}); }, []);

  // Edit mode: load the existing connection + its items and prefill.
  useEffect(() => {
    if (!editing) return;
    api.get(`/connections/${connectionId}`).then(({ data }) => {
      const items = data.billable_items || [];
      const primary = items.find((i) => !["AMC", "POP Upstream"].includes(i.item_type));
      const amcItem = items.find((i) => i.item_type === "AMC");
      setCust({ id: data.customer_id, name: data.customer_name });
      setF((p) => ({
        ...p,
        delivery_type: data.delivery_type || p.delivery_type,
        pop_id: data.pop_id || "",
        site_name: data.location_name || "", city: data.city || "",
        service_type: primary?.item_type || "ILL",
        vendor_account_id: primary?.isp_account_number || "", vendor_circuit_id: primary?.isp_circuit_id || "",
        vendor_mobile: primary?.registered_mobile || "", bandwidth: primary?.bandwidth || "",
        billing_type: primary?.billing_type || "Prepaid", renewal_cycle: primary?.renewal_cycle || "",
        date_field: (primary?.billing_type === "Postpaid" ? primary?.next_payment_due : primary?.current_expiry_date) || "",
        arc: primary?.arc ?? "", otc: primary?.otc ?? "",
        amc_billing_type: amcItem?.billing_type || "Postpaid", amc_cycle: amcItem?.renewal_cycle || "",
        amc_date: (amcItem?.billing_type === "Postpaid" ? amcItem?.next_payment_due : amcItem?.current_expiry_date) || "",
        amc_arc: amcItem?.arc ?? "",
      }));
      if (primary?.vendor_id) setVend({ id: primary.vendor_id, name: primary.vendor_name });
      if (amcItem?.vendor_id) setAmcVend({ id: amcItem.vendor_id, name: amcItem.vendor_name });
    }).catch(() => toast.error("Could not load this link"));
  }, [editing, connectionId]);

  const ownPop = isOwnPop(f.delivery_type);
  const amc = isAmc(f.delivery_type);

  const submit = async (confirm = {}) => {
    if (!cust.id && !cust.name) { toast.error("Pick or enter a customer."); return; }
    if (ownPop && !f.pop_id) { toast.error("Own-POP delivery needs a POP."); return; }
    if (!ownPop && !vend.id && !vend.name) { toast.error("Pick or enter a vendor."); return; }
    const payload = {
      customer_id: cust.id || null, customer_name: cust.id ? null : cust.name,
      customer_code: f.customer_code, account_manager: f.account_manager, customer_mobile: f.customer_mobile,
      site_name: f.site_name, site_code: f.site_code, city: f.city, state: f.state,
      delivery_type: f.delivery_type, pop_id: ownPop ? f.pop_id : null,
      confirm_new_customer: confirm.customer || false,
      confirm_new_vendor: confirm.vendor || false, confirm_new_amc_vendor: confirm.amc || false,
    };
    if (!ownPop) {
      Object.assign(payload, {
        vendor_id: vend.id || null, vendor_name: vend.id ? null : vend.name,
        service_type: f.service_type || "ILL", vendor_account_id: f.vendor_account_id,
        vendor_circuit_id: f.vendor_circuit_id, vendor_mobile: f.vendor_mobile, bandwidth: f.bandwidth,
        billing_type: f.billing_type, renewal_cycle: f.renewal_cycle, renewal_cycle_days: f.renewal_cycle_days ? Number(f.renewal_cycle_days) : null,
        current_expiry_date: f.billing_type === "Postpaid" ? null : f.date_field,
        next_payment_due: f.billing_type === "Postpaid" ? f.date_field : null,
        arc: canRates && f.arc ? Number(f.arc) : null, otc: canRates && f.otc ? Number(f.otc) : null,
      });
    }
    if (amc) {
      Object.assign(payload, {
        amc_vendor_id: amcVend.id || null, amc_vendor_name: amcVend.id ? null : amcVend.name,
        amc_billing_type: f.amc_billing_type, amc_cycle: f.amc_cycle,
        amc_next_payment_due: f.amc_billing_type === "Postpaid" ? f.amc_date : null,
        amc_current_expiry_date: f.amc_billing_type === "Postpaid" ? null : f.amc_date,
        amc_arc: canRates && f.amc_arc ? Number(f.amc_arc) : null,
      });
    }
    setSaving(true);
    try {
      const { data } = editing
        ? await api.put(`/customer-link/${connectionId}`, payload)
        : await api.post("/customer-link", payload);
      toast.success(editing ? "Customer link updated" : "Customer link created");
      nav(`/customers/${data.connection.customer_id}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.needs_confirmation) {
        const who = d.needs_confirmation.includes("amc") ? "amc" : d.needs_confirmation.includes("vendor") ? "vendor" : "customer";
        const names = (d.suggestions || []).map((s) => s.name).join(", ");
        if (window.confirm(`Possible duplicate ${who}: ${names}.\n\nClick OK to create “${d.typed}” as NEW anyway, or Cancel to go back and pick the existing one.`)) {
          return submit({ ...confirm, [who]: true });
        }
      } else {
        toast.error(apiError(d));
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-head text-2xl font-extrabold">{editing ? "Edit Customer Link" : "Add Customer Link"}</h1>
        <button onClick={() => nav(-1)} className="text-slate-400 hover:text-slate-600"><X /></button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6">
        {/* 1 Customer & site */}
        <Section n="1" title="Customer & site">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              {editing ? (
                <div>
                  <label className="text-xs font-semibold text-slate-600">Customer</label>
                  <div className={field + " bg-slate-100 text-slate-600"} data-testid="link-customer-locked">{cust.name || "—"}</div>
                  <div className="text-[11px] text-slate-400 mt-1">Customer can't be changed when editing a link.</div>
                </div>
              ) : (
                <OrgPicker label="Customer" matchUrl="/match/customers" value={cust} testid="link-customer"
                  onPick={(r) => setCust({ id: r.id, name: r.name })} onNew={(name) => setCust(name ? { name, isNew: true } : {})} />
              )}
            </div>
            <div><label className="text-xs font-semibold text-slate-600">Site / location</label><input className={field} data-testid="link-site" value={f.site_name || ""} onChange={(e) => set("site_name", e.target.value)} /></div>
            <div><label className="text-xs font-semibold text-slate-600">Site code</label><input className={field} value={f.site_code || ""} onChange={(e) => set("site_code", e.target.value)} /></div>
            <div><label className="text-xs font-semibold text-slate-600">City</label><input className={field} value={f.city || ""} onChange={(e) => set("city", e.target.value)} /></div>
            <div><label className="text-xs font-semibold text-slate-600">State</label><input className={field} value={f.state || ""} onChange={(e) => set("state", e.target.value)} /></div>
            <div><label className="text-xs font-semibold text-slate-600">Account manager</label><input className={field} value={f.account_manager || ""} onChange={(e) => set("account_manager", e.target.value)} /></div>
            <div><label className="text-xs font-semibold text-slate-600">Customer contact mobile</label><input className={field} value={f.customer_mobile || ""} onChange={(e) => set("customer_mobile", e.target.value)} /></div>
          </div>
        </Section>

        {/* 2 Delivery */}
        <Section n="2" title="Delivery type">
          <select className={field} data-testid="link-delivery" value={f.delivery_type} onChange={(e) => set("delivery_type", e.target.value)}>
            {DELIVERY.map((d) => <option key={d}>{d}</option>)}
          </select>
        </Section>

        {/* 3 POP or Vendor */}
        {ownPop ? (
          <Section n="3" title="POP" note="Own-POP delivery">
            <select className={field} data-testid="link-pop" value={f.pop_id || ""} onChange={(e) => set("pop_id", e.target.value)}>
              <option value="">— pick a POP —</option>
              {pops.map((p) => <option key={p.id} value={p.id}>{p.name}{p.location ? ` — ${p.location}` : ""}</option>)}
            </select>
          </Section>
        ) : (
          <Section n="3" title="Vendor service" note="vendor delivery">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-3">
                <OrgPicker label="Vendor" matchUrl="/match/vendors" value={vend} testid="link-vendor"
                  onPick={(r) => setVend({ id: r.id, name: r.name })} onNew={(name) => setVend(name ? { name, isNew: true } : {})} />
              </div>
              <div><label className="text-xs font-semibold text-slate-600">Service</label><select className={field} value={f.service_type || "ILL"} onChange={(e) => set("service_type", e.target.value)}><option>ILL</option><option>Broadband</option><option>Other</option></select></div>
              <div><label className="text-xs font-semibold text-slate-600">Bandwidth</label><input className={field} value={f.bandwidth || ""} onChange={(e) => set("bandwidth", e.target.value)} /></div>
              <div><label className="text-xs font-semibold text-slate-600">Billing</label><select className={field} value={f.billing_type} onChange={(e) => set("billing_type", e.target.value)}><option>Prepaid</option><option>Postpaid</option></select></div>
              <div><label className="text-xs font-semibold text-slate-600">Vendor account ID</label><input className={field} data-testid="link-acct" value={f.vendor_account_id || ""} onChange={(e) => set("vendor_account_id", e.target.value)} /></div>
              <div><label className="text-xs font-semibold text-slate-600">Vendor circuit ID</label><input className={field} value={f.vendor_circuit_id || ""} onChange={(e) => set("vendor_circuit_id", e.target.value)} /></div>
              <div><label className="text-xs font-semibold text-slate-600">Vendor-registered mobile</label><input className={field} value={f.vendor_mobile || ""} onChange={(e) => set("vendor_mobile", e.target.value)} /></div>
              <div><label className="text-xs font-semibold text-slate-600">Billing cycle</label><input className={field} placeholder="Monthly, Quarterly…" value={f.renewal_cycle || ""} onChange={(e) => set("renewal_cycle", e.target.value)} /></div>
              <div><label className="text-xs font-semibold text-slate-600">{f.billing_type === "Postpaid" ? "Next due" : "Current expiry"} (DD-MM-YYYY→ISO)</label><input className={field} type="date" value={f.date_field || ""} onChange={(e) => set("date_field", e.target.value)} /></div>
              <RateInput label="ARC — annual (₹)" canRates={canRates && !editing} value={f.arc} onChange={(v) => set("arc", v)} />
              <RateInput label="OTC — one-time (₹)" canRates={canRates && !editing} value={f.otc} onChange={(v) => set("otc", v)} />
            </div>
          </Section>
        )}

        {/* 4 AMC */}
        {amc && (
          <Section n="4" title="AMC" note="+ AMC delivery">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-3">
                <OrgPicker label="AMC vendor" matchUrl="/match/vendors" value={amcVend} testid="link-amcvendor"
                  onPick={(r) => setAmcVend({ id: r.id, name: r.name })} onNew={(name) => setAmcVend(name ? { name, isNew: true } : {})} />
              </div>
              <div><label className="text-xs font-semibold text-slate-600">Billing</label><select className={field} value={f.amc_billing_type} onChange={(e) => set("amc_billing_type", e.target.value)}><option>Postpaid</option><option>Prepaid</option></select></div>
              <div><label className="text-xs font-semibold text-slate-600">Cycle</label><input className={field} placeholder="Yearly…" value={f.amc_cycle || ""} onChange={(e) => set("amc_cycle", e.target.value)} /></div>
              <div><label className="text-xs font-semibold text-slate-600">{f.amc_billing_type === "Prepaid" ? "Current expiry" : "Next due"}</label><input className={field} type="date" value={f.amc_date || ""} onChange={(e) => set("amc_date", e.target.value)} /></div>
              <RateInput label="ARC — annual (₹)" canRates={canRates && !editing} value={f.amc_arc} onChange={(v) => set("amc_arc", v)} />
            </div>
          </Section>
        )}

        <div className="border-t border-slate-200 mt-6 pt-5 flex justify-end gap-3">
          <button onClick={() => nav(-1)} className="px-4 h-10 rounded-lg border text-sm">Cancel</button>
          <button onClick={() => submit()} disabled={saving} data-testid="link-save" className="px-5 h-10 rounded-lg bg-[var(--brand)] text-white text-sm font-semibold disabled:opacity-60">Save customer link</button>
        </div>
        {!canRates && <p className="text-xs text-slate-400 text-right mt-2">You can save the link; an Accounts Manager can add ARC/OTC later.</p>}
      </div>
    </div>
  );
}

function RateInput({ label, canRates, value, onChange }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600">{label} {!canRates && <span className="text-[10px] text-slate-400">(locked)</span>}</label>
      <input className={field + (canRates ? "" : " bg-slate-100 border-dashed")} type="number" disabled={!canRates}
        value={value || ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
